/**
 * Teams & captains — the organizer's window on an event's teams (Oct 26, 2026 — Dennis;
 * claude/Captain_Team_Link_Plan_v1.md, Slice 1).
 *
 * EventTeamsButton sits on the event's card next to ✉️ Welcome email. It shows only when the
 * event has teams, and carries an "N changes" badge for changes captains and golfers made
 * that the organizer hasn't marked seen. Organizer only: App.js doesn't render it on the
 * Event Desk.
 *
 * The window:
 *   - 🔒 Lock teams / Unlock (pressed by the organizer, never automatic). Locked = only the
 *     organizer and the Event Desk can change names and emails; it's the signal to print the
 *     final scorecards and cart signs.
 *   - "Each golfer needs:" Name (always), Email, GHIN #, Tee — one setting per event.
 *   - Changes from captains and golfers, newest first, with "Mark all as seen".
 *   - Every team: captain, how complete, and who still needs what.
 *   - Captain's Welcome (Oct 28, 2026 — Slice 2): one email to each captain with his own link
 *     to "My team". Send to captains not sent yet; per team, when it went out, Send / Resend and
 *     Copy link. The text is the org's "Captain's Welcome" template, edited here.
 *
 * API: GET /events/:id/teams, PUT /events/:id/teams/settings, POST /events/:id/teams/changes/seen,
 *      GET/PUT /events/:id/teams/captain-welcome, POST /events/:id/teams/captain-welcome/send,
 *      POST /events/:id/teams/:teamId/captain-link
 */
import React, { useEffect, useRef, useState } from "react";

const GREEN = "#2d6a4f";
const INK = "#111827";
const MUTED = "#6b7280";
const FIELD_NAMES = { name: "name", email: "email", ghin: "GHIN #", tee: "tee" };
const OPTION_LABELS = { email: "Email", ghin: "GHIN #", tee: "Tee" };
const pill = (bg, border, color) => ({ padding: "5px 12px", borderRadius: 999, background: bg, border: `1px solid ${border}`, color, fontSize: 13, fontWeight: 600 });
const CW_LABELS = {
  captain_first_name: "Captain's first name", captain_name: "Captain's full name", team_name: "Team name",
  team_size: "Team size", team_link: "His team link", event_name: "Event name", event_date: "Date", organizer_name: "Your name",
};

function headers(getToken, json) {
  const h = {};
  if (json) h["Content-Type"] = "application/json";
  const t = getToken && getToken();
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
}

async function call(apiUrl, getToken, path, method = "GET", body) {
  const res = await fetch(`${apiUrl}${path}`, {
    method,
    credentials: "include",
    headers: headers(getToken, !!body),
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch (_) { /* empty */ }
  if (!res.ok) {
    const e = new Error((data && data.error) || "Something went wrong. Please try again.");
    e.status = res.status;
    throw e;
  }
  return data;
}

/** GET /events/:id/teams, for the print page's "teams aren't locked" reminder. */
export function fetchEventTeams(apiUrl, getToken, eventId) {
  return call(apiUrl, getToken, `/events/${eventId}/teams`);
}

function when(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d)) return "";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/** One line of the change log, in plain words. */
export function describeChange(c) {
  const who = c.player_name || "A golfer";
  const team = c.team_name ? ` (${c.team_name})` : "";
  switch (c.kind) {
    case "named":
      return `${who} was named on the team${team}${c.old_name ? `, in the spot held as “${c.old_name}”` : ""}.`;
    case "details":
      return `${who}'s details were filled in${team}.`;
    case "corrected": {
      const parts = [];
      if (c.old_name && c.old_name !== c.player_name) parts.push(`name corrected from “${c.old_name}”`);
      if (c.old_email && c.old_email !== (c.player_email || "")) parts.push(`email changed from ${c.old_email}`);
      return `${who}${team}: ${parts.length ? parts.join("; ") : "details corrected"}.`;
    }
    case "replaced":
      return `${who} replaced ${c.old_name || "a golfer"}${team}.`;
    case "added":
      return `${who} was added to the team${team}.`;
    // Oct 28, 2026: a captain signed up a team through the invite link. old_name carries the
    // one-line summary ("3 golfers named · 1 name to come").
    case "signup":
      return `${who} signed up a team online${team}${c.old_name ? ` — ${c.old_name}` : ""}.`;
    case "signup_pair":
      return `${who} signed up online and asked to be paired up${team}${c.old_name ? ` — ${c.old_name}` : ""}.`;
    case "self": {
      const parts = [];
      if (c.old_name && c.old_name !== c.player_name) parts.push(`name from “${c.old_name}”`);
      if ((c.old_email || "") !== (c.player_email || "")) parts.push(c.old_email ? `email from ${c.old_email}` : "added an email");
      return `${who}${team} changed his own ${parts.length ? parts.join(" and ") : "details"}.`;
    }
    default:
      return `${who}${team} changed.`;
  }
}

export function EventTeamsButton({ eventId, apiUrl, getToken, style, isReadOnly = false, onReadOnlyClick }) {
  const [info, setInfo] = useState(null);   // { teams, unseen }
  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!eventId) return undefined;
    let off = false;
    fetchEventTeams(apiUrl, getToken, eventId)
      .then((d) => { if (!off) setInfo({ teams: d.totals ? d.totals.teams : 0, unseen: d.unseen_count || 0, locked: !!d.locked }); })
      .catch(() => { if (!off) setInfo(null); });
    return () => { off = true; };
  }, [eventId, apiUrl, getToken, tick]);
  if (!eventId || !info || !info.teams) return null;
  return (
    <>
      <button
        type="button"
        data-testid="event-teams-button"
        onClick={() => { if (isReadOnly) { if (onReadOnlyClick) onReadOnlyClick(); return; } setOpen(true); }}
        title="Your teams and captains — lock the teams, choose what each golfer needs, and see the changes captains made"
        style={{ fontSize: 12, fontWeight: 600, padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", color: "#1f4d36", border: "1px solid #b9c9bf", background: "#fff", opacity: isReadOnly ? 0.5 : 1, display: "inline-flex", alignItems: "center", gap: 6, ...(style || {}) }}
      >
        {info.locked ? "🔒" : "👥"} Teams
        {info.unseen > 0 && (
          <span data-testid="event-teams-badge" style={{ fontSize: 11, fontWeight: 800, background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5", borderRadius: 999, padding: "1px 7px" }}>
            {info.unseen} change{info.unseen === 1 ? "" : "s"}
          </span>
        )}
      </button>
      {open && <EventTeamsModal eventId={eventId} apiUrl={apiUrl} getToken={getToken} onClose={() => { setOpen(false); setTick((t) => t + 1); }} />}
    </>
  );
}

/**
 * The Captain's Welcome text: preview with the first captain's details, and edit. Saving writes
 * the org's "Captain's Welcome" template, so the next event starts from the same words.
 */
function CaptainWelcomeText({ cw, busy, setBusy, onSaved, setMsg, apiUrl, getToken, eventId, btn, onDirty }) {
  const [editing, setEditing] = useState(false);
  const [preview, setPreview] = useState(false);
  const [subject, setSubject] = useState(cw.subject || "");
  const [body, setBody] = useState(cw.body || "");
  const bodyRef = useRef(null);
  const dirty = subject !== cw.subject || body !== cw.body;
  useEffect(() => { if (onDirty) onDirty(dirty); }, [dirty, onDirty]);
  const maxBody = cw.max_body || 2500;

  const fillSample = (text) => {
    const first = (cw.captains || []).find((c) => c.can_email) || (cw.captains || [])[0];
    const name = (first && first.captain_name) || "Pat Golfer";
    const values = {
      ...(cw.sample || {}), captain_name: name, captain_first_name: String(name).split(" ")[0],
      team_name: (first && first.team_name) || "Your team", team_size: (first && first.size) || 4,
      team_link: "https://teetimeorganizer.net/portal?token=his-own-link&team=1",
    };
    let out = String(text || "");
    (cw.placeholders || []).forEach((k) => { out = out.split(`{${k}}`).join(values[k] != null ? String(values[k]) : ""); });
    return out;
  };

  const save = async (reset) => {
    setBusy(true); setMsg(null);
    try {
      const d = await call(apiUrl, getToken, `/events/${eventId}/teams/captain-welcome`, "PUT", reset ? { reset: true } : { subject, body });
      onSaved(d);
      setSubject(d.subject || ""); setBody(d.body || "");
      setMsg({ text: reset ? "Back to the standard Captain's email." : "Captain's email saved." });
      if (!reset) setEditing(false);
    } catch (e) {
      setMsg({ text: e.message, error: true });
    } finally {
      setBusy(false);
    }
  };

  const insert = (key) => {
    const tag = `{${key}}`;
    const el = bodyRef.current;
    if (!el) { setBody((b) => b + tag); return; }
    const start = el.selectionStart != null ? el.selectionStart : body.length;
    const end = el.selectionEnd != null ? el.selectionEnd : body.length;
    setBody(body.slice(0, start) + tag + body.slice(end));
    setTimeout(() => { try { el.focus(); el.selectionStart = el.selectionEnd = start + tag.length; } catch (_) { /* fine */ } }, 0);
  };

  const small = (disabled) => ({ ...btn(false, disabled), padding: "6px 12px", fontSize: 13 });
  return (
    <div style={{ marginTop: 12, borderTop: "1px solid #f1f5f9", paddingTop: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 240px", fontSize: 13, color: INK }}>
          <span style={{ color: MUTED }}>Subject:</span> {fillSample(subject)}
          {dirty ? <span style={{ color: "#b45309", fontWeight: 600 }}> (not saved yet)</span> : cw.is_default ? <span style={{ color: MUTED }}> (standard text)</span> : null}
        </div>
        <button type="button" data-testid="cw-preview-toggle" onClick={() => setPreview((p) => !p)} style={small(false)}>{preview ? "Hide preview" : "Preview"}</button>
        <button type="button" data-testid="cw-edit-toggle" onClick={() => { if (editing) { setSubject(cw.subject || ""); setBody(cw.body || ""); } setEditing((e) => !e); }} style={small(false)}>{editing ? (dirty ? "Close without saving" : "Close editor") : "Edit the email"}</button>
      </div>
      {preview && (
        <div data-testid="cw-preview" style={{ marginTop: 10, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: 12, fontSize: 13.5, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 360, overflowY: "auto", color: INK }}>
          {fillSample(body)}
        </div>
      )}
      {editing && (
        <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
          <div style={{ fontSize: 12.5, color: "#374151", background: "#f0f9f3", border: "1px solid #c2dacc", borderRadius: 8, padding: "8px 10px", lineHeight: 1.45 }}>
            This is your organization&rsquo;s <strong>Captain&rsquo;s email</strong>. Saving it here keeps it for your next event too.
          </div>
          <label style={{ fontSize: 13, fontWeight: 600, color: INK }}>Subject
            <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} data-testid="cw-subject"
              style={{ display: "block", width: "100%", boxSizing: "border-box", marginTop: 4, padding: "8px 10px", fontSize: 14, border: "1px solid #d1d5db", borderRadius: 8, fontFamily: "inherit" }} />
          </label>
          <label style={{ fontSize: 13, fontWeight: 600, color: INK }}>Message <span style={{ fontWeight: 500, color: body.length > maxBody ? "#b91c1c" : "#9ca3af" }}>· {body.length} of {maxBody} characters</span>
            <textarea ref={bodyRef} value={body} onChange={(e) => setBody(e.target.value)} rows={14} data-testid="cw-body"
              style={{ display: "block", width: "100%", boxSizing: "border-box", marginTop: 4, padding: 10, fontSize: 13.5, border: "1px solid #d1d5db", borderRadius: 8, fontFamily: "inherit", lineHeight: 1.45, resize: "vertical" }} />
          </label>
          <div style={{ fontSize: 12, color: MUTED }}>Tap to insert:</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {(cw.placeholders || []).map((k) => (
              <button key={k} type="button" onClick={() => insert(k)} style={{ fontSize: 12, padding: "4px 9px", borderRadius: 999, border: "1px solid #c2dacc", background: "#fff", color: "#1f4d36", cursor: "pointer", fontFamily: "inherit" }}>{CW_LABELS[k] || k}</button>
            ))}
          </div>
          <div style={{ fontSize: 12, color: MUTED }}>Keep &ldquo;His team link&rdquo; in the email — it&rsquo;s each captain&rsquo;s own link.</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" data-testid="cw-save" disabled={busy || !dirty} onClick={() => save(false)} style={btn(true, busy || !dirty)}>Save</button>
            {!cw.is_default && <button type="button" data-testid="cw-reset" disabled={busy} onClick={() => save(true)} style={btn(false, busy)}>Use the standard text</button>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function EventTeamsModal({ eventId, apiUrl, getToken, onClose }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);   // { text, error }
  const [confirmLock, setConfirmLock] = useState(null);   // true = locking, false = unlocking
  const [cw, setCw] = useState(null);         // GET /events/:id/teams/captain-welcome
  const [cwError, setCwError] = useState("");
  const [cwResult, setCwResult] = useState(null);   // last send: { sent, skipped, failed }
  const [cwConfirm, setCwConfirm] = useState(false);
  const [copied, setCopied] = useState(null);   // { team_id, link, ok }
  const [cwDirty, setCwDirty] = useState(false);   // the Captain's Welcome editor has unsaved changes

  useEffect(() => {
    let off = false;
    fetchEventTeams(apiUrl, getToken, eventId)
      .then((d) => { if (!off) setData(d); })
      .catch((e) => { if (!off) setLoadError(e.message); });
    call(apiUrl, getToken, `/events/${eventId}/teams/captain-welcome`)
      .then((d) => { if (!off) setCw(d); })
      .catch((e) => { if (!off) setCwError(e.message); });
    return () => { off = true; };
  }, [eventId, apiUrl, getToken]);

  // Send the Captain's Welcome: {} = every captain not sent yet; { team_ids } = these teams.
  const sendCaptains = async (body) => {
    setBusy(true); setMsg(null); setCwResult(null);
    try {
      const d = await call(apiUrl, getToken, `/events/${eventId}/teams/captain-welcome/send`, "POST", body || {});
      if (d.captain_welcome) setCw(d.captain_welcome);
      setCwResult({ sent: d.sent || [], skipped: d.skipped || [], failed: d.failed || [] });
      const n = (d.sent || []).length;
      setMsg(n > 0
        ? { text: `Captain's email sent to ${n} captain${n === 1 ? "" : "s"}.` }
        : { text: "Nobody was sent the email — see the notes under Captain's email.", error: true });
    } catch (e) {
      setMsg({ text: e.message, error: true });
    } finally {
      setBusy(false);
      setCwConfirm(false);
    }
  };

  const copyLink = async (teamId) => {
    setBusy(true); setCopied(null);
    try {
      const d = await call(apiUrl, getToken, `/events/${eventId}/teams/${teamId}/captain-link`, "POST");
      let ok = false;
      try { await navigator.clipboard.writeText(d.link); ok = true; } catch (_) { /* shown below to copy by hand */ }
      setCopied({ team_id: teamId, link: d.link, ok });
    } catch (e) {
      setMsg({ text: e.message, error: true });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const settings = async (body, okText) => {
    setBusy(true); setMsg(null);
    try {
      const d = await call(apiUrl, getToken, `/events/${eventId}/teams/settings`, "PUT", body);
      setData(d);
      setMsg({ text: okText });
    } catch (e) {
      setMsg({ text: e.message, error: true });
    } finally {
      setBusy(false);
      setConfirmLock(null);
    }
  };

  const markSeen = async () => {
    setBusy(true); setMsg(null);
    try {
      const d = await call(apiUrl, getToken, `/events/${eventId}/teams/changes/seen`, "POST");
      setData(d);
    } catch (e) {
      setMsg({ text: e.message, error: true });
    } finally {
      setBusy(false);
    }
  };

  const toggleRequired = (k) => {
    if (!data) return;
    const next = data.required.includes(k) ? data.required.filter((x) => x !== k) : [...data.required, k];
    settings({ required_fields: next }, "Saved — each golfer needs: " + ["Name", ...next.map((x) => OPTION_LABELS[x])].join(", ") + ".");
  };

  const section = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 16px", marginBottom: 14 };
  const h2 = { margin: "0 0 10px", fontSize: 15, fontWeight: 800, color: INK };
  const btn = (primary, disabled) => ({ padding: "9px 16px", fontSize: 14, fontWeight: 700, borderRadius: 8, cursor: disabled ? "default" : "pointer", fontFamily: "inherit", border: primary ? "none" : "1px solid #d1d5db", background: disabled ? "#9ca3af" : (primary ? GREEN : "#fff"), color: primary || disabled ? "#fff" : "#374151" });

  const unseen = data ? data.changes.filter((c) => !c.seen_at) : [];
  const seen = data ? data.changes.filter((c) => c.seen_at) : [];

  return (
    <div role="dialog" aria-modal="true" aria-label="Teams" data-testid="event-teams-modal"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 2500, background: "rgba(17,24,39,0.5)", display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "4vh 12px", overflowY: "auto" }}>
      <div style={{ background: "#f9fafb", width: "100%", maxWidth: 760, borderRadius: 14, boxShadow: "0 10px 40px rgba(0,0,0,0.25)", overflow: "hidden" }}>
        <div style={{ background: GREEN, color: "#fff", padding: "14px 18px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1, fontSize: 18, fontWeight: 800 }}>👥 Teams & captains</div>
          <button type="button" onClick={onClose} disabled={busy} style={{ ...btn(false, false), background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.5)" }}>Close</button>
        </div>
        <div style={{ padding: 16 }}>
          {loadError && <div role="alert" style={{ color: "#b42318", fontWeight: 600 }}>{loadError}</div>}
          {!data && !loadError && <div style={{ color: MUTED }}>Loading…</div>}
          {msg && <div role={msg.error ? "alert" : "status"} style={{ marginBottom: 12, fontSize: 14, fontWeight: 600, color: msg.error ? "#b42318" : "#166534" }}>{msg.error ? "" : "✓ "}{msg.text}</div>}

          {data && (
            <>
              {/* Lock */}
              <div style={{ ...section, borderColor: data.locked ? "#86efac" : "#fcd34d", background: data.locked ? "#f0fdf4" : "#fffbeb" }} data-testid="teams-lock">
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 280px", fontSize: 14, color: INK, lineHeight: 1.5 }}>
                    {data.locked ? (
                      <>
                        <strong>🔒 Teams are locked</strong>{data.locked_at ? ` since ${when(data.locked_at)}` : ""}{data.locked_by ? ` by ${data.locked_by}` : ""}.
                        <div style={{ color: MUTED, fontSize: 13 }}>Only you and the Event Desk can change names and emails. Print the final scorecards and cart signs.</div>
                      </>
                    ) : (
                      <>
                        <strong>🔓 Teams are open</strong> — captains can fill in and change their teammates, and golfers can correct their own name and email.
                        <div style={{ color: MUTED, fontSize: 13 }}>Lock the teams when the field is final, then print the scorecards and cart signs.</div>
                      </>
                    )}
                  </div>
                  {confirmLock === null ? (
                    <button type="button" data-testid="teams-lock-button" disabled={busy} onClick={() => setConfirmLock(!data.locked)} style={btn(!data.locked, busy)}>
                      {data.locked ? "Unlock teams" : "🔒 Lock teams"}
                    </button>
                  ) : (
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: INK }}>{confirmLock ? "Lock the teams now?" : "Unlock — captains can change names again?"}</span>
                      <button type="button" data-testid="teams-lock-confirm" disabled={busy} onClick={() => settings({ locked: confirmLock }, confirmLock ? "Teams locked." : "Teams unlocked.")} style={btn(true, busy)}>{confirmLock ? "Yes, lock" : "Yes, unlock"}</button>
                      <button type="button" disabled={busy} onClick={() => setConfirmLock(null)} style={btn(false, busy)}>Cancel</button>
                    </div>
                  )}
                </div>
              </div>

              {/* Captain's Welcome */}
              <div style={section} data-testid="captain-welcome">
                <div style={h2}>✉️ Captain&rsquo;s email</div>
                <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.5, marginBottom: 10 }}>
                  One email to each team&rsquo;s captain with his own link to his team, so he can add his teammates&rsquo; names and emails. His teammates aren&rsquo;t emailed. Send the field&rsquo;s welcome later, from ✉️ Field welcome.
                </div>
                {cwError && <div role="alert" style={{ color: "#b42318", fontSize: 13, fontWeight: 600 }}>{cwError}</div>}
                {!cw && !cwError && <div style={{ color: MUTED, fontSize: 13 }}>Loading…</div>}
                {cw && (
                  <>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                      <span style={pill("#fff", "#d1d5db", INK)}>{cw.counts.teams} captain{cw.counts.teams === 1 ? "" : "s"}</span>
                      <span style={pill("#e6f4ea", "#bfe0c9", "#1f5a3c")}>{cw.counts.sent} sent</span>
                      <span style={pill(cw.counts.not_sent ? "#fff6df" : "#fff", cw.counts.not_sent ? "#efd594" : "#d1d5db", cw.counts.not_sent ? "#7a5410" : "#374151")}>{cw.counts.not_sent} not sent yet</span>
                      {cw.counts.cannot_email > 0 && <span style={pill("#fef2f2", "#fecaca", "#991b1b")}>{cw.counts.cannot_email} can&rsquo;t be emailed</span>}
                    </div>
                    {cw.locked && (
                      <div style={{ fontSize: 13, color: "#92400e", marginBottom: 10 }}>The teams are locked, so captains can only look at their teams now. Unlock them first if captains still need to make changes.</div>
                    )}
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      {!cwConfirm ? (
                        <button type="button" data-testid="cw-send-unsent" disabled={busy || cwDirty || !cw.counts.not_sent} onClick={() => setCwConfirm(true)} style={btn(true, busy || cwDirty || !cw.counts.not_sent)}>
                          {cw.counts.not_sent ? `Send to ${cw.counts.not_sent} captain${cw.counts.not_sent === 1 ? "" : "s"} not sent yet` : "Every captain has been sent it"}
                        </button>
                      ) : (
                        <>
                          <span style={{ fontSize: 13, fontWeight: 600, color: INK }}>Send the Captain&rsquo;s email to {cw.counts.not_sent} captain{cw.counts.not_sent === 1 ? "" : "s"} now?</span>
                          <button type="button" data-testid="cw-send-confirm" disabled={busy} onClick={() => sendCaptains({})} style={btn(true, busy)}>{busy ? "Sending…" : "Yes, send"}</button>
                          <button type="button" disabled={busy} onClick={() => setCwConfirm(false)} style={btn(false, busy)}>Cancel</button>
                        </>
                      )}
                    </div>
                    {cwDirty && <div style={{ marginTop: 8, fontSize: 13, color: "#b45309", fontWeight: 600 }}>Save the email, or close the editor without saving, before you send.</div>}
                    {cwResult && (cwResult.skipped.length > 0 || cwResult.failed.length > 0) && (
                      <div data-testid="cw-result" style={{ marginTop: 10, fontSize: 13, lineHeight: 1.5 }}>
                        {cwResult.skipped.map((s) => <div key={`s${s.team_id}`} style={{ color: "#92400e" }}>Not sent — {s.team_name}{s.name ? ` (${s.name})` : ""}: {s.reason}. Use Copy link below and send it yourself.</div>)}
                        {cwResult.failed.map((s) => <div key={`f${s.team_id}`} style={{ color: "#b42318" }}>Failed — {s.team_name}{s.name ? ` (${s.name})` : ""}: {s.reason}. Try Resend.</div>)}
                      </div>
                    )}
                    <CaptainWelcomeText cw={cw} busy={busy} setBusy={setBusy} onSaved={setCw} setMsg={setMsg} apiUrl={apiUrl} getToken={getToken} eventId={eventId} btn={btn} onDirty={setCwDirty} />
                  </>
                )}
              </div>

              {/* Required fields */}
              <div style={section} data-testid="teams-required">
                <div style={h2}>Each golfer needs:</div>
                <div style={{ display: "flex", gap: 18, flexWrap: "wrap", fontSize: 14, color: INK }}>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 6, color: MUTED }}>
                    <input type="checkbox" checked disabled /> Name (always)
                  </label>
                  {(data.required_options || ["email", "ghin", "tee"]).map((k) => (
                    <label key={k} style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: busy ? "default" : "pointer" }}>
                      <input type="checkbox" checked={data.required.includes(k)} disabled={busy} onChange={() => toggleRequired(k)} /> {OPTION_LABELS[k] || k}
                    </label>
                  ))}
                </div>
                <div style={{ fontSize: 12, color: MUTED, marginTop: 8, lineHeight: 1.45 }}>Shown to captains as “Needs: …” on their team. It never stops anyone from playing.</div>
              </div>

              {/* Changes */}
              <div style={section} data-testid="teams-changes">
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <div style={{ ...h2, margin: 0, flex: 1 }}>Changes from captains and golfers{unseen.length ? ` · ${unseen.length} new` : ""}</div>
                  {unseen.length > 0 && <button type="button" data-testid="teams-mark-seen" disabled={busy} onClick={markSeen} style={btn(false, busy)}>Mark all as seen</button>}
                </div>
                {data.changes.length === 0 && <div style={{ fontSize: 13, color: MUTED }}>No changes yet.</div>}
                {[...unseen, ...seen].map((c) => (
                  <div key={c.id} style={{ display: "flex", gap: 10, padding: "8px 0", borderTop: "1px solid #f1f5f9", fontSize: 13, lineHeight: 1.45, color: c.seen_at ? MUTED : INK }}>
                    <span style={{ width: 8, height: 8, marginTop: 6, borderRadius: 999, flex: "0 0 auto", background: c.seen_at ? "transparent" : "#dc2626" }} />
                    <div style={{ flex: 1 }}>
                      <div>{describeChange(c)}</div>
                      {c.kind === "replaced" && c.old_had_welcome && (
                        <div style={{ color: "#b45309", fontWeight: 600 }}>⚠️ {c.old_name || "The golfer taken off"} already had the field welcome — let him know.</div>
                      )}
                      {(c.kind === "replaced" || c.kind === "added" || c.kind === "named" || c.kind === "signup" || c.kind === "signup_pair") && (
                        <div style={{ color: MUTED, fontSize: 12 }}>{String(c.kind).startsWith("signup") ? "This team hasn't had" : `${c.player_name || "The new golfer"} hasn't had`} the field welcome — send it from ✉️ Field welcome.</div>
                      )}
                      <div style={{ color: MUTED, fontSize: 12 }}>{[c.by_name ? `by ${c.by_name}${c.by_role === "captain" ? " (captain)" : ""}` : "", when(c.created_at)].filter(Boolean).join(" · ")}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Teams */}
              <div style={section} data-testid="teams-list">
                <div style={h2}>
                  {data.totals.teams} team{data.totals.teams === 1 ? "" : "s"} · {data.totals.complete} of {data.totals.golfers} golfers complete
                  {data.totals.open_spots ? ` · ${data.totals.open_spots} open spot${data.totals.open_spots === 1 ? "" : "s"}` : ""}
                </div>
                {data.teams.map((t) => {
                  const incomplete = t.members.filter((m) => m.missing && m.missing.length);
                  const c = cw ? (cw.captains || []).find((x) => x.team_id === String(t.id)) : null;
                  return (
                    <div key={t.id} style={{ padding: "10px 0", borderTop: "1px solid #f1f5f9", fontSize: 13, lineHeight: 1.45 }}>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "baseline" }}>
                        <strong style={{ fontSize: 14, color: INK }}>{t.name}</strong>
                        <span style={{ color: MUTED }}>Captain: {t.captain ? (t.captain.name || "—") : "none set"}{t.captain && !t.captain.email ? " (no email)" : ""}</span>
                        <span style={{ marginLeft: "auto", fontWeight: 700, color: t.complete === t.size ? "#166534" : "#92400e" }}>{t.complete} of {t.size} complete</span>
                      </div>
                      {incomplete.length > 0 && (
                        <div style={{ color: MUTED }}>
                          {incomplete.map((m) => `${m.held ? (m.name || "Held spot") : m.name}: needs ${m.missing.map((k) => FIELD_NAMES[k] || k).join(", ")}`).join(" · ")}
                        </div>
                      )}
                      {t.open_spots > 0 && <div style={{ color: MUTED }}>{t.open_spots} open spot{t.open_spots === 1 ? "" : "s"}</div>}
                      {c && (
                        <div data-testid={`cw-team-${t.id}`} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 6 }}>
                          <span style={{ fontWeight: 600, color: c.sent_at ? "#166534" : (c.can_email ? "#92400e" : "#991b1b") }}>
                            ✉️ {c.sent_at ? `Captain's email sent ${when(c.sent_at)}` : (c.can_email ? "Captain's email not sent" : `Can't email: ${String(c.reason || "").toLowerCase()}`)}
                          </span>
                          {c.can_email && (
                            <button type="button" data-testid={`cw-resend-${t.id}`} disabled={busy || cwDirty} onClick={() => sendCaptains({ team_ids: [String(t.id)] })} style={{ ...btn(false, busy || cwDirty), padding: "6px 12px", fontSize: 13 }}>
                              {c.sent_at ? "Resend" : "Send"}
                            </button>
                          )}
                          {c.captain_player_id && (
                            <button type="button" data-testid={`cw-copy-${t.id}`} disabled={busy} onClick={() => copyLink(String(t.id))} style={{ ...btn(false, busy), padding: "6px 12px", fontSize: 13 }}>
                              Copy link
                            </button>
                          )}
                        </div>
                      )}
                      {copied && copied.team_id === String(t.id) && (
                        <div style={{ marginTop: 6 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: copied.ok ? "#166534" : INK }}>{copied.ok ? "✓ Copied — paste it into your own email or text to the captain." : "Select the link and copy it:"}</div>
                          <input readOnly value={copied.link} onFocus={(e) => e.target.select()} data-testid={`cw-link-${t.id}`}
                            style={{ width: "100%", boxSizing: "border-box", marginTop: 4, padding: "6px 8px", fontSize: 12, border: "1px solid #d1d5db", borderRadius: 6, fontFamily: "inherit", color: INK }} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
